<template>
  <v-container
    class="ma-0 pa-0 white"
    fluid
    fill-height
  >
    <v-layout>
      <v-flex>
        <fullpage-loader
          message="טוען מחולצים..."
          v-if="loading"
        ></fullpage-loader>

        <customer
          :comp-id="compId"
          v-else-if="customerId"
        />

        <div v-else>
          <v-data-table
            :headers="headers.filter(item => shouldShow(item.roles, user))"
            :items="customers"
            item-key="_id"
            hide-actions
            must-sort
          >
            <template v-slot:items="props">
              <td class="font-weight-bold">
                <a @click="showCustomer(props.item._id)">
                  {{ props.item.name }}
                  <span v-if="isAllEventsMode && props.item.event" class="caption grey--text">
                    ({{ getEventName(props.item.event) }})
                  </span>
                </a>
              </td>
              <td class="text-xs-right">{{ props.item.id || 'לא ידוע' }}</td>
              <td class="text-xs-right">{{ props.item.insuranceCompany ? props.item.insuranceCompany.name : 'לא ידוע' }}</td>
              <td class="text-xs-right">{{ props.item.status || 'לא ידוע' }}</td>
              <td class="text-xs-right">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                      class="previous-events-count"
                      style="cursor: pointer;"
                      @click="openEventsArchive(props.item)"
                    >
                      {{ getPreviousEventsCount(props.item) }}
                    </span>
                  </template>
                  <span style="white-space: pre-line;">{{ getPreviousEventsTooltip(props.item) }}</span>
                </v-tooltip>
              </td>
              <td
                class="text-xs-left"
                width="30"
                v-if="shouldShow(['ADMIN', 'EVENT_MANAGER'], user)"
              >
                <v-btn
                  small
                  icon
                  class="ml-0 mr-0 mt-1"
                  :loading="removing[props.item._id]"
                  @click="remove(props.item._id)"
                >
                  <v-icon small>delete</v-icon>
                </v-btn>
              </td>
            </template>
          </v-data-table>

          <v-btn
            small
            @click="create"
            class="mt-3"
            v-if="shouldShow(['ADMIN', 'EVENT_MANAGER'], user)"
          >
            <v-icon>add</v-icon> הוספת מחולצ/ת
          </v-btn>
        </div>
      </v-flex>
    </v-layout>

  </v-container>
</template>

<script>
import FullpageLoader from "../../FullpageLoader.vue";
import Customer from "./Customers/Customer.vue";
import generateId from "@/idGenerator";
import shouldShow from "@/utils/shouldShow";
import { getApi } from "@/api";
import { format } from "date-fns";

export default {
  data() {
    return {
      loading: true,
      compId: null,
      headers: [
        {
          text: "שם",
          align: "right",
          value: "name"
        },
        { text: "מס׳ זהות", align: "right", value: "id" },
        { text: "חברת ביטוח", align: "right", value: "insurance" },
        { text: "סטטוס", align: "right", value: "status" },
        { text: "אירועים קודמים", align: "right", value: "previousEvents" },
        {
          text: "מחיקה",
          align: "left",
          sortable: false,
          roles: ["ADMIN", "EVENT_MANAGER"]
        }
      ],
      previousEvents: {}
    };
  },

  computed: {
    isAllEventsMode() {
      const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
      return eventIdsToShow && eventIdsToShow.length > 1;
    },
    customers() {
      // Get event IDs to show from store
      const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
      
      if (eventIdsToShow.length === 1) {
        // Single event mode
        return this.$store.getters["customers/eventCustomers"](this.eventId);
      } else {
        // Multiple events mode (כולם) - get customers from all events
        const allCustomers = [];
        eventIdsToShow.forEach(eventId => {
          const eventCustomers = this.$store.getters["customers/eventCustomers"](eventId);
          if (eventCustomers && eventCustomers.length > 0) {
            allCustomers.push(...eventCustomers);
          }
        });
        return allCustomers;
      }
    },
    customerId() {
      return this.$store.getters["customers/activeCustomersIds"][this.compId];
    },
    resource() {
      return this.$store.state.resources.customer;
    },
    entityEditDialog: {
      get() {
        return this.$store.state.entityEditDialog;
      },
      set(payload) {
        this.$store.commit("TOGGLE_ENTITY_EDIT_DIALOG", payload);
      }
    },
    activeCustomerId: {
      get() {
        return this.$store.getters["customers/activeCustomersIds"][this.compId];
      },
      set(payload) {
        this.$store.commit("customers/SET_ACTIVE_CUSTOMER_ID", {
          compId: this.compId,
          payload
        });
      }
    },
    user() {
      return this.$store.getters["user/user"];
    }
  },
  methods: {
    shouldShow,
    formatIsoDate(date) {
      if (!date) return "לא ידוע";
      try {
        return format(new Date(date), "DD/MM/YYYY");
      } catch (e) {
        return "לא ידוע";
      }
    },
    getCustomerIdentity(customer) {
      return (customer && (customer.identityNumber || customer.id)) || null;
    },
    getPreviousEventsCount(customer) {
      const identity = this.getCustomerIdentity(customer);
      if (!identity) return "—";
      const entry = this.previousEvents[identity];
      if (!entry) return "...";
      if (entry.loading) return "...";
      return entry.count;
    },
    getPreviousEventsTooltip(customer) {
      const identity = this.getCustomerIdentity(customer);
      if (!identity) return "אין מספר זהות";
      const entry = this.previousEvents[identity];
      if (!entry) return "טוען אירועים קודמים";
      if (entry.loading) return "טוען אירועים קודמים";
      return entry.tooltip || "אין אירועים קודמים";
    },
    openEventsArchive(customer) {
      const identity = this.getCustomerIdentity(customer);
      if (!identity) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: "אין מספר זהות להצגה",
          color: "error"
        });
        return;
      }

      const entry = this.previousEvents[identity];
      if (!entry || entry.loading) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: "טוען אירועים קודמים...",
          color: "info"
        });
        return;
      }

      if (!entry.events || !entry.events.length) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: "לא נמצאו אירועים קודמים",
          color: "info"
        });
        return;
      }

      const eventIds = [
        ...new Set(
          entry.events
            .map(event => event._id || event.id)
            .filter(Boolean)
        )
      ];

      if (!eventIds.length) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: "לא נמצאו מזהים תקינים לאירועים",
          color: "error"
        });
        return;
      }

      this.$router.push({
        path: "/events/archive",
        query: {
          _id: eventIds.join(","),
          q: JSON.stringify({ _id: { $in: eventIds } })
        }
      });
    },
    getEventName(eventId) {
      const event = this.$store.getters["entity"]("events", eventId);
      return event ? (event.title || `אירוע ${eventId}`) : `אירוע ${eventId}`;
    },
    showCustomer(id) {
      this.activeCustomerId = id;
    },
    fetchCustomers() {
      this.loading = true;
      this.removing = [];
      this.previousEvents = {};
      
      // Get event IDs to show from store
      const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
      console.log('Customers fetchCustomers - eventIdsToShow:', eventIdsToShow);
      
      this.$store
        .dispatch("API_GET", {
          url: "/customers",
          entityType: "customer",
          schema: "customerListSchema",
          params: {
            q: JSON.stringify({ event: { $in: eventIdsToShow } }),
            limit: 0
          },
          replace: true
        })
        .catch(err => {
          this.$store.dispatch("SHOW_SNACKBAR", {
            text: err.message,
            color: "error"
          });
        })
        .finally(() => {
          this.loading = false;
          this.loadPreviousEvents();
        });
    },
    create() {
      debugger;
      
      // Check if we're in כולם mode (multiple events)
      const eventIdsToShow = this.$store.getters["events/eventIdsToShow"];
      const isAllEventsMode = eventIdsToShow && eventIdsToShow.length > 1;

      if (isAllEventsMode) {
        // Create customer for all events in the ARAN
        console.log('Customers create - creating for all events:', eventIdsToShow);
        
        // Create multiple entities for all events
        const entities = eventIdsToShow.map(eventId => ({
          _id: generateId(),
          event: eventId
        }));

        // Open dialog with multiple entities
        this.edit({
          entity: entities[0], // Use first one for the dialog
          isNew: true,
          createForAllEvents: true,
          allEventIds: eventIdsToShow
        });
      } else {
        // Create customer for single event (normal mode)
        let newEntity = { _id: generateId(), event: this.eventId };
        this.edit({
          entity: newEntity,
          isNew: true
        });
      }
    },
    edit({ entity, entityId, isNew = false, createForAllEvents = false, allEventIds = null }) {
      debugger;
      if (!entity) {
        entity = this.$store.state.entities[this.resource.entityType][entityId];
      }
      this.$store.commit("ADD_EDITABLE_ENTITY", {
        entity,
        schema: this.resource.schema
      });
      this.$store.commit("SET_ENTITY_EDIT_DATA", {
        id: entity._id,
        resource: this.resource,
        isNew,
        createForAllEvents,
        allEventIds
      });
      this.entityEditDialog = true;
    },
    remove(id) {
      this.$confirm("האם למחוק " + this.resource.name).then(res => {
        if (!res) {
          return;
        }
        this.removeConfirmed(id);
      });
    },
    removeConfirmed(id) {
      this.removing[id] = true;
      this.$store
        .dispatch("REMOVE_ENTITY", {
          url: this.resource.endpoint,
          entityType: this.resource.entityType,
          id
        })
        .then(() => {
          this.$store.dispatch("SHOW_SNACKBAR", {
            text: this.resource.name + " נמחק/ה",
            color: "success"
          });
        })
        .catch(err => {
          this.$store.dispatch("SHOW_SNACKBAR", {
            text: err.response ? err.response.message : err.message,
            color: "error"
          });
        })
        .finally(() => {
          this.removing[id] = false;
        });
    },
    loadPreviousEvents() {
      const identities = new Set();
      this.customers.forEach(item => {
        const identity = this.getCustomerIdentity(item);
        if (identity) identities.add(identity);
      });

      identities.forEach(identity => {
        if (!this.previousEvents[identity]) {
          this.$set(this.previousEvents, identity, {
            loading: true,
            count: 0,
            events: [],
            tooltip: "טוען אירועים קודמים"
          });
          this.fetchPreviousEvents(identity);
        }
      });
    },
    getCurrentEventIdsToExclude() {
      const eventIdsToShow = this.$store.getters["events/eventIdsToShow"] || [];
      const ids = new Set(eventIdsToShow.filter(Boolean));
      if (this.eventId) {
        ids.add(this.eventId);
      }
      return ids;
    },
    async fetchPreviousEvents(identity) {
      try {
        const api = await getApi();
        const res = await api.post("/customers/events-by-identity", {
          identityNumber: identity
        });
        const allEvents = (res && res.data && res.data.events) || [];
        const currentEventIds = this.getCurrentEventIdsToExclude();
        const events = allEvents.filter(event => {
          const id = event && (event._id || event.id);
          return id && !currentEventIds.has(id);
        });
        const tooltip = events.length
          ? events
              .slice(0, 3)
              .map(
                event =>
                  `${event.title || "אירוע"} (${this.formatIsoDate(event.startDate)} - ${this.formatIsoDate(
                    event.endDate
                  )})`
              )
              .join("\n")
          : "אין אירועים קודמים";
        this.$set(this.previousEvents, identity, {
          loading: false,
          count: events.length,
          events,
          tooltip
        });
      } catch (err) {
        console.error("Failed to load previous events", err);
        this.$set(this.previousEvents, identity, {
          loading: false,
          count: 0,
          events: [],
          tooltip: "שגיאה בטעינת אירועים קודמים"
        });
      }
    }
  },
  mounted() {
    this.fetchCustomers();
    this.compId = generateId();
    
    // Listen for global refetch event
    this.$bus.$on('refetch-all-data', () => {
      console.log('Customers - received global refetch event');
      this.fetchCustomers();
    });
  },

  watch: {
    // Refetch when event IDs to show change
    '$store.state.events.eventIdsToShow': {
      handler(newVal, oldVal) {
        console.log('Customers watcher - eventIdsToShow changed from:', oldVal, 'to:', newVal);
        this.fetchCustomers();
      },
      deep: true,
      immediate: true
    },
    // Watch for route changes to refetch data when switching events
    '$route.params.id': {
      handler(newEventId) {
        if (newEventId && newEventId !== this.eventId) {
          this.eventId = newEventId;
          this.fetchCustomers();
        }
      },
      immediate: true
    },
    customers: {
      handler() {
        this.loadPreviousEvents();
      },
      immediate: true
    }
    // customers: function(val, oldVal) {
    //   if (val.length === 1) {
    //     this.activeCustomerId = val[0]._id;
    //   }
    // }
  },

  beforeDestroy() {
    // Clean up global event listener
    this.$bus.$off('refetch-all-data');
  },

  props: ["eventId"],

  components: {
    FullpageLoader,
    Customer
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
.customer-container .v-toolbar__content {
  padding: 0 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/events/modules/Customers.vue